import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { TrendingUp } from 'lucide-react';

export interface EarningsTrendPoint {
  key: string;
  label: string;
  year: number;
  month: number;
  amount: number;
  sessions: number;
  hours: number;
  isSelected: boolean;
}

interface EarningsTrendChartProps {
  data: EarningsTrendPoint[];
}

const formatCurrency = (value: number) =>
  value >= 1000 ? `$${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k` : `$${value.toFixed(0)}`;

const EarningsTrendChart = ({ data }: EarningsTrendChartProps) => {
  const peak = useMemo(() => Math.max(...data.map((d) => d.amount), 0), [data]);
  const total = useMemo(() => data.reduce((sum, d) => sum + d.amount, 0), [data]);

  if (data.every((d) => d.amount === 0)) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl p-6">
        <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-indigo-500" />
          12-Month Earnings Trend
        </h4>
        <p className="text-center text-sm text-gray-400 py-8">No earnings recorded in the last 12 months.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 sm:p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-indigo-500" />
            12-Month Earnings Trend
          </h4>
          <p className="text-xs text-gray-400 mt-1">
            Rolling total across all groups · ${total.toFixed(0)} over 12 months
          </p>
        </div>
      </div>
      <div className="h-52 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} interval={0} />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 10, fill: '#9ca3af' }}
              width={48}
            />
            <Tooltip
              cursor={{ fill: 'rgba(99, 102, 241, 0.06)' }}
              contentStyle={{
                borderRadius: 12,
                border: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                fontSize: 12,
              }}
              formatter={(value: number) => [`$${value.toFixed(2)}`, 'Earnings']}
              labelFormatter={(_, payload) => {
                const point = payload?.[0]?.payload as EarningsTrendPoint | undefined;
                if (!point) return '';
                return `${point.label} · ${point.sessions} sessions · ${point.hours.toFixed(1)}h`;
              }}
            />
            <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
              {data.map((entry) => (
                <Cell
                  key={entry.key}
                  fill={entry.isSelected ? '#4f46e5' : '#a5b4fc'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      {peak > 0 && (
        <p className="text-[10px] text-gray-400 mt-2 text-center">
          Peak month: ${peak.toFixed(0)} · highlighted bar is the month you&apos;re viewing
        </p>
      )}
    </div>
  );
};

export default EarningsTrendChart;
